import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { PasswordInput } from "@/components/ui/PasswordInput";
import { SectionContainer } from "@/components/marketing/SectionContainer";
import { useAuth } from "@/contexts/AuthContext";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { onboardingApi, ApiError } from "@/lib/api";
import { isFreeEmailDomain } from "@/lib/emailDomains";
import { usePageMeta } from "./usePageMeta";

const schema = z.object({
  email: z
    .string()
    .email("Enter a valid email")
    .refine((v) => !isFreeEmailDomain(v), {
      message: "Please use your business email",
    }),
  password: z.string().min(8, "At least 8 characters"),
});
type FormValues = z.infer<typeof schema>;

export function Register() {
  usePageMeta({
    title: "Create your account — VirtualDesk.ai",
    description:
      "Sign up with your work email to start hiring virtual employees. Verification takes one click.",
  });

  const [params] = useSearchParams();
  const plan = params.get("plan");
  const role = params.get("role");
  const navigate = useNavigate();

  const { signUp, deleteCurrentUser } = useAuth();
  const setOnboarding = useOnboardingStore((s) => s.setOnboarding);
  const setPreselectedAgent = useOnboardingStore((s) => s.setPreselectedAgent);
  const reset = useOnboardingStore((s) => s.reset);

  // Fresh register clears stale onboarding state from any prior abandoned run
  // on this origin.
  useEffect(() => {
    reset();
    if (role) setPreselectedAgent(role);
  }, [reset, setPreselectedAgent, role]);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: "onBlur",
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = async (values: FormValues) => {
    // Step 1 — Firebase account.
    try {
      await signUp(values.email, values.password);
    } catch (err) {
      const message = (err as { message?: string }).message ?? "Something went wrong";
      console.error("signUp", err);
      toast.error("Sign-up failed", { description: message });
      return;
    }

    // Step 2 — Backend onboarding row + verification email. The backend
    // hard-requires email delivery in prod and either succeeds with
    // verification_email_sent=true or fails 4xx/5xx. On any failure we roll
    // back the Firebase user too so the email isn't burned against an
    // orphan account.
    //
    // We never advance to /register/check-email unless the backend has
    // confirmed the verification email was actually delivered. The previous
    // "continue with Firebase only" branch was the bug that produced false
    // "Check your inbox" pages with no email behind them.
    let res;
    try {
      res = await onboardingApi.register(values.email, values.password);
    } catch (err) {
      const ae = err as ApiError;
      console.error("onboarding.register", ae);
      try {
        await deleteCurrentUser();
      } catch (rollbackErr) {
        console.error("rollback firebase user", rollbackErr);
      }
      const description =
        ae.code === "email_provider_unavailable"
          ? "We can't send verification emails right now. Please try again in a few minutes or contact support."
          : ae.code === "email_send_failed"
          ? "We couldn't deliver your verification email. Please try again in a moment."
          : ae.status === 0
          ? "We couldn't reach VirtualDesk. Check your connection and try again."
          : ae.message || "Please try again.";
      toast.error("Registration failed", { description });
      return;
    }

    if (!res.verification_email_sent) {
      // 201 but no email actually delivered (only happens with the dev noop
      // sender). Don't lie about a "Check your inbox" page in prod.
      if (res.dev_verify_url) {
        setOnboarding(res.onboarding_id, values.email);
        const search = new URLSearchParams();
        search.set("email", values.email);
        search.set("dev_verify_url", res.dev_verify_url);
        if (plan) search.set("plan", plan);
        if (role) search.set("role", role);
        toast.message("Dev mode — email not actually sent", {
          description: `Use the verify URL from the response (provider: ${res.verification_email_provider}).`,
        });
        navigate(`/register/check-email?${search.toString()}`, { replace: true });
        return;
      }
      try {
        await deleteCurrentUser();
      } catch (rollbackErr) {
        console.error("rollback firebase user", rollbackErr);
      }
      toast.error("Registration failed", {
        description: "We couldn't send your verification email. Please try again.",
      });
      return;
    }

    setOnboarding(res.onboarding_id, values.email);
    toast.success("Verification email sent", {
      description: `We sent a link to ${values.email}.`,
    });
    const search = new URLSearchParams();
    search.set("email", values.email);
    if (plan) search.set("plan", plan);
    if (role) search.set("role", role);
    navigate(`/register/check-email?${search.toString()}`, { replace: true });
  };

  const emailError = form.formState.errors.email?.message;
  const passwordError = form.formState.errors.password?.message;

  return (
    <SectionContainer>
      <div className="mx-auto max-w-[480px]">
        <div className="text-center">
          <p className="text-sm font-semibold uppercase tracking-wider text-brand-violet">
            {plan ? `${plan.charAt(0).toUpperCase()}${plan.slice(1)} plan` : "Get started"}
          </p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl tracking-wide leading-[0.95] text-brand-ink">
            Create your account.
          </h1>
          <p className="mt-4 text-base text-brand-ink/70">
            Use your work email. We'll set up your workspace in under ten minutes.
          </p>
        </div>

        <div className="mt-10 rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8">
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-brand-violet/15 p-2 text-brand-violet">
              <UserPlus className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-brand-ink">Register</h2>
              <p className="text-xs text-brand-ink/60">
                Email + password. Verification takes one click.
              </p>
            </div>
          </div>

          <form
            className="mt-6 space-y-4"
            onSubmit={form.handleSubmit(onSubmit)}
            noValidate
          >
            <div className="space-y-1.5">
              <Label htmlFor="email">Work email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                aria-invalid={!!emailError}
                aria-describedby={emailError ? "email-error" : undefined}
                {...form.register("email")}
              />
              {emailError && (
                <p id="email-error" className="text-xs text-signal-failed">
                  {emailError}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                autoComplete="new-password"
                aria-invalid={!!passwordError}
                aria-describedby={passwordError ? "password-error" : undefined}
                {...form.register("password")}
              />
              {passwordError && (
                <p id="password-error" className="text-xs text-signal-failed">
                  {passwordError}
                </p>
              )}
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Create account
            </Button>

            <p className="text-xs text-brand-ink/50 text-center">
              By creating an account you agree to our{" "}
              <Link to="/terms" className="underline hover:text-brand-ink">
                Terms
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="underline hover:text-brand-ink">
                Privacy Policy
              </Link>
              .
            </p>
          </form>
        </div>

        <p className="mt-6 text-center text-sm text-brand-ink/60">
          Already have a workspace?{" "}
          <Link
            to="/onboarding/login"
            className="text-brand-violet hover:text-brand-violet/80"
          >
            Sign in
          </Link>
        </p>
      </div>
    </SectionContainer>
  );
}
